import React, { FC, Fragment } from 'react'
import Button from '@vtex/styleguide/lib/Button'
import Modal from '@vtex/styleguide/lib/Modal'
import { FilterStatus } from '@/checkout-instore-legacy/search/typings'
import { FormattedMessage } from 'react-intl'
import { useMediaQuery } from '@/checkout-instore-legacy/hooks/mediaQuery'
import { FilterAttributes } from '@/checkout-instore-legacy/components/SearchPage/SearchContent'
import {
  Button as UIButton,
  Flex,
  FlexSpacer,
  IconX,
  csx,
} from '@vtex/admin-ui'
import FiltersLoading from './FiltersLoading'
import FiltersError from './FiltersError'
import FiltersFade from './FiltersFade'

type FilterSidebarProps = {
  term: string
  status: FilterStatus
  openModal: boolean
  closeModal: () => void
  submitFilters: () => void
  clearFilters: () => void
  reloadFilters: (term: string, attributes: FilterAttributes) => void
  toggleable?: boolean
}

export const FilterSidebar: FC<FilterSidebarProps> = ({
  term,
  status,
  openModal,
  closeModal,
  submitFilters,
  clearFilters,
  reloadFilters,
  toggleable = false,
  children,
}) => {
  const isButtonsDisabled = status !== 'resolved'
  const { value: device } = useMediaQuery({
    s: 'phone',
    ns: 'tablet',
    l: 'desktop',
  })

  const actionButtons = (
    <div>
      <span className="mr5">
        <Button
          testId="search-filters-clear-button"
          variation="tertiary"
          size="small"
          onClick={clearFilters}
          disabled={isButtonsDisabled}
        >
          <FormattedMessage id="clearFilters" />
        </Button>
      </span>
      <Button
        testId="search-filters-submit-button"
        variation="primary"
        size="small"
        onClick={submitFilters}
        disabled={isButtonsDisabled}
      >
        <FormattedMessage id="filterSearch" />
      </Button>
    </div>
  )

  const desktopFilter = (
    <div
      data-testid="search-filters-sidebar"
      className={`${
        toggleable ? 'w-100 h-100' : 'w-33-m w-30-l w-25-xl'
      } dn-s bg-base flex-ns flex-column bl-s b--silver c-on-base`}
    >
      <div className="overflow-y-scroll hide-scrollbar flex-grow-1 ph6 pv5">
        <div className="w-100 pt2">
          <span className="f4 fw5">
            <Flex justify="space-around" align="center">
              <FormattedMessage id="filters" />
              <FlexSpacer />
              {toggleable && (
                <UIButton
                  icon={<IconX title="Close" />}
                  aria-label="Close Filter Button"
                  variant="neutralTertiary"
                  onClick={closeModal}
                />
              )}
            </Flex>
          </span>
        </div>
        {status === 'loading' ? (
          <FiltersLoading />
        ) : status === 'error' ? (
          <FiltersError term={term} reloadFilters={reloadFilters} />
        ) : (
          children
        )}
      </div>
      {status === 'error' ? null : (
        <div className="flex justify-end pv4 ph6 bt-s b--silver flex-grow-0">
          {actionButtons}
        </div>
      )}
    </div>
  )

  switch (device) {
    case 'phone':
      return (
        <Modal
          isOpen={openModal}
          title={<FormattedMessage id="filters" />}
          responsiveFullScreen
          bottomBar={
            <div data-testid="search-filters-mobile" className="nv2 nh3 nowrap">
              {actionButtons}
            </div>
          }
          onClose={closeModal}
        >
          <span data-testid="search-filters-mobile">{children}</span>
        </Modal>
      )
    case 'desktop':
    case 'tablet':
      return toggleable ? (
        <Fragment>
          <FiltersFade onClick={closeModal} />
          <div
            className={csx({
              position: 'absolute',
              width: '360px',
              height: '100%',
              right: '$space-0',
              zIndex: 9999,
            })}
          >
            {desktopFilter}
          </div>
        </Fragment>
      ) : (
        desktopFilter
      )
    default:
      return null
  }
}
